<template>
  <!-- prettier-ignore -->
  <div
    class="filter"
    @click="search"
  >
    <basic-text-input
      ref="inputRef"
      v-model="selectedLabel"
      manual-validation
      :name="`input-${$props.name}`"
      required
      :label="$props.label"
      :size="$props.size"
      :supporting-text="$props.supportingText"
      :v="$props.v"
      @blur="onBlur"
      @input.stop="onInput"
      @keydown="onKeydown"
    >
      <template #trailing>
        <the-icon
          art="light"
          :name="dropdownRef?.isOpen ? 'chevron-up' : 'chevron-down'"
        />
      </template>
    </basic-text-input>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      manual-focus
      :name="`menu-${$props.name}`"
      :filterable="true"
      :options="results"
      :selected="model"
      :size="$props.size"
      :trigger="inputRef?.inputTriggerRef"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
import { nextTick, ref, reactive, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { ReactivityUtil } from '@/utils/Reactivity'

import BasicDropdownMenu from '@/components/Basic/DropdownMenu'
import BasicTextInput from '@/components/Basic/TextInput'

// INIT
const model = defineModel({ type: [String, Number] })
const props = defineProps({
  label: {
    type: String,
    required: true,
  },

  name: {
    type: String,
    required: true,
  },

  options: {
    type: Array,
    required: true,
  },

  size: {
    validator(value) {
      return ['sm', 'md'].includes(value)
    },
    default: 'sm',
  },

  supportingText: {
    type: String,
    default: undefined,
  },

  v: {
    type: Object,
    default: () => {},
  },
})

// DATA
const KEY = uuidv4()
const dropdownId = `dropdown-${KEY}`
const dropdownRef = ref(null)
const selectedLabel = ref(null)
const inputRef = ref(null)

const results = reactive([])

// METHODS

function selectItem(item, doFocus = true) {
  if (!item) return

  selectedLabel.value = item.label
  model.value = item.value

  if (doFocus) {
    inputRef.value.inputRef.focus()
  }
}

function onInput() {
  // If input is being modified, clear the model value to allow new search
  if (model.value !== null && model.value !== undefined) {
    model.value = null
  }
  search()
}

async function search() {
  ReactivityUtil.resetData(results)

  await nextTick()

  const searchList = filterResults(selectedLabel.value)
  ReactivityUtil.reAssign(results, searchList)

  if (results.length > 0) {
    // Multiple results
    dropdownRef.value.open()
  }
}

function filterResults(searchTerm) {
  if (!searchTerm) return props.options

  const sanitizedSearchTerm = searchTerm.toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const searchRegex = new RegExp(`^${sanitizedSearchTerm}`, 'i')

  const filteredResults = props.options.filter(option => {
    if (Number.isInteger(option.label)) {
      return option.label.toString().match(searchRegex)
    } else {
      return option.label.match(searchRegex)
    }
  })

  // no results
  if (filteredResults.length === 0) {
    model.value = null
  }

  return filteredResults
}

function onBlur() {
  if (model.value === undefined || model.value === null || model.value.toString() !== selectedLabel?.value.toString()) {
    const searchList = filterResults(selectedLabel.value)

    // @note: check, if only one result and FilterInput matches the label of the result
    if (
      searchList.length === 1 &&
      searchList[0].label.toString().toLowerCase() === selectedLabel.value.toString().toLowerCase()
    ) {
      // @note: only select item, if not already selected
      if (model.value !== searchList[0].value) {
        selectItem(searchList[0], false)
      }
    } else if (searchList.length === 0) {
      selectedLabel.value = null
    }
  }
}

function onKeydown(event) {
  // Check if we have a valid selection and prevent additional character input
  const hasValidSelection = model.value !== null && model.value !== undefined

  // Check if the current text matches exactly a selected option (meaning it's truly selected, not just typed)
  const selectedOption = hasValidSelection ? props.options.find(option => option.value === model.value) : null
  const isExactMatch = selectedOption && selectedLabel.value === selectedOption.label

  // Allow deletion keys even when selection exists
  const isDeletionKey = ['Backspace', 'Delete'].includes(event.code)

  // Allow navigation and special keys
  const isNavigationKey = ['Tab', 'ArrowUp', 'ArrowDown', 'Enter', 'NumpadEnter', 'Escape'].includes(event.code)

  // Handle deletion when we have a valid selection that exactly matches - clear everything at once
  // This mimics the behavior of the X icon in TextInput
  if (hasValidSelection && isExactMatch && isDeletionKey) {
    selectedLabel.value = null // This triggers the watcher which handles the rest
    event.preventDefault()
    return
  }

  // Prevent typing additional characters when we have a valid selection
  if (hasValidSelection && !isDeletionKey && !isNavigationKey && event.key.length === 1) {
    event.preventDefault()
    return
  }

  // @note: dropdown is open, handle events with filtered set
  if (dropdownRef.value.isOpen) {
    const searchList = filterResults(selectedLabel.value)
    switch (event.code) {
      case 'Tab':
        if (searchList.length > 0) {
          dropdownRef.value.setFocus()
          event.preventDefault()
        }
        break

      case 'ArrowUp':
      case 'ArrowDown':
        dropdownRef.value.setFocus()
        event.preventDefault()
        break

      case 'Enter':
      case 'NumpadEnter':
        selectItem(results[0])
        dropdownRef.value.close()
        event.preventDefault()
        break
    }

    // @note: dd still closed, open will all results
  } else {
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowDown':
        search()
        event.preventDefault()
        break
    }
  }
}

// WATCHERS
watch(
  model,
  value => {
    if (value && selectedLabel.value !== value) {
      selectedLabel.value = props.options.find(x => x.value === value)?.label
    }
  },
  { immediate: true }
)

watch(
  () => selectedLabel.value,
  value => {
    if (value === null) {
      // model has been cleared in textinput, therefore clear the model here as well
      model.value = null
      ReactivityUtil.reAssign(results, props.options)
      dropdownRef.value.open()
    }
  }
)
</script>

<style scoped>
.filter {
  position: relative;
  display: inline-block;
  width: 100%;
}
</style>
